// src/components/RestaurantMenu.tsx
import type { FC } from "react";
import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import {
  Container,
  Stack,
  Text,
  Title,
  SimpleGrid,
  Box,
  Flex,
  Anchor,
  Badge,
  Divider,
  useMantineColorScheme,
  ActionIcon,
  ScrollArea,
  Paper,
  Group,
  TextInput,
  Button,
  Chip,
  Loader,
  Center,
  SegmentedControl,
  Table,
} from "@mantine/core";
import {
  IconMapPin,
  IconPhone,
  IconMoonStars,
  IconSun,
  IconCategory,
  IconSearch,
  IconX,
  IconArrowLeft,
  IconArrowRight,
  IconApps,
  IconLayoutGrid,
  IconList,
} from "@tabler/icons-react";
import type { RestaurantMenuData } from "../types";
import { MenuItemCard } from "./MenuItemCard";
import { MenuItemListItem } from "./MenuItemListItem";
import { Empty } from "./Empty";
import { ChatAssistant } from "./ChatAssistant";

interface RestaurantMenuProps {
  menuData: RestaurantMenuData;
}

const ITEMS_PER_PAGE = 12;

export const RestaurantMenu: FC<RestaurantMenuProps> = ({ menuData }) => {
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const categories = menuData?.menus?.categories || [];
  const [activeCategory, setActiveCategory] = useState<string | null>("all");
  const [mounted, setMounted] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDietary, setSelectedDietary] = useState<string[]>([]);
  const [visibleItems, setVisibleItems] = useState(ITEMS_PER_PAGE);
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const scrollRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Check if mobile
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  // Add "All" category at the beginning
  const allCategories = useMemo(() => {
    const allItems = categories.flatMap((category) =>
      category.items.map((item) => ({
        ...item,
        categoryName: category.name,
      })),
    );

    return [
      {
        name: "all",
        displayName: "All",
        description: "All menu items",
        items: allItems,
        icon: <IconApps size={24} />,
      },
      ...categories.map((category) => ({
        name: category.name,
        displayName: category.name,
        description: category.description,
        items: category.items.map((item) => ({
          ...item,
          categoryName: category.name,
        })),
        icon: <IconCategory size={24} />,
      })),
    ];
  }, [categories]);

  // Get current category items
  const currentCategoryItems = useMemo(() => {
    const category = allCategories.find((c) => c.name === activeCategory);
    return category?.items || [];
  }, [activeCategory, allCategories]);

  // Filter items based on search and dietary preferences
  const filteredItems = useMemo(() => {
    let items = currentCategoryItems;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      items = items.filter(
        (item) =>
          item.name.toLowerCase().includes(query) ||
          item.description?.toLowerCase().includes(query) ||
          item.categoryName?.toLowerCase().includes(query),
      );
    }

    if (selectedDietary.includes("veg")) {
      items = items.filter((item) => item.isVegetarian === true);
    }
    if (selectedDietary.includes("nonVeg")) {
      items = items.filter((item) => item.isVegetarian === false);
    }

    return items;
  }, [currentCategoryItems, searchQuery, selectedDietary]);

  const paginatedItems = useMemo(() => {
    return filteredItems.slice(0, visibleItems);
  }, [filteredItems, visibleItems]);

  const hasMore = visibleItems < filteredItems.length;

  const loadMore = useCallback(() => {
    if (hasMore && !isLoading) {
      setIsLoading(true);
      setTimeout(() => {
        setVisibleItems((prev) =>
          Math.min(prev + ITEMS_PER_PAGE, filteredItems.length),
        );
        setIsLoading(false);
      }, 500);
    }
  }, [hasMore, isLoading, filteredItems.length]);

  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          loadMore();
        }
      },
      { threshold: 0.1 },
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, isLoading, loadMore]);

  useEffect(() => {
    setVisibleItems(ITEMS_PER_PAGE);
  }, [activeCategory, searchQuery, selectedDietary]);

  useEffect(() => {
    setMounted(true);
  }, []);

  const scrollToCategory = (categoryName: string) => {
    setActiveCategory(categoryName);
    setVisibleItems(ITEMS_PER_PAGE);
  };

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -200, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 200, behavior: "smooth" });
    }
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedDietary([]);
  };

  const hasActiveFilters = searchQuery !== "" || selectedDietary.length > 0;

  if (!mounted) {
    return null;
  }

  if (categories.length === 0) {
    return <Empty height={400} text="No menu items available" />;
  }

  const currentCategoryInfo = allCategories.find(
    (c) => c.name === activeCategory,
  );

  return (
    <Box
      style={{
        backgroundColor: colorScheme === "dark" ? "#1a1a1a" : "#f8f9fa",
        minHeight: "100vh",
      }}
    >
      {/* Theme Toggle Button */}
      <ActionIcon
        variant="filled"
        color="orange"
        onClick={() => toggleColorScheme()}
        size="lg"
        radius="xl"
        style={{
          position: "fixed",
          bottom: isMobile ? 130 : 150,
          right: 20,
          zIndex: 1000,
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        }}
      >
        {colorScheme === "dark" ? (
          <IconSun size={18} />
        ) : (
          <IconMoonStars size={18} />
        )}
      </ActionIcon>

      <Container size="xl" py="xl">
        {/* Restaurant Header */}
        <Stack gap="md" mb="xl" ta="center">
          <Title
            order={1}
            size="h1"
            style={{ fontSize: "clamp(2rem, 5vw, 3rem)" }}
          >
            {menuData.name}
          </Title>

          <Flex justify="center" gap="lg" wrap="wrap">
            {menuData.location && (
              <Anchor
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                  menuData.location,
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                size="sm"
                c="dimmed"
                style={{ display: "flex", alignItems: "center", gap: 5 }}
              >
                <IconMapPin size={16} />
                {menuData.location}
              </Anchor>
            )}

            {menuData.contactNo && (
              <Anchor
                href={`tel:${menuData.contactNo.replace(/\s/g, "")}`}
                size="sm"
                c="dimmed"
                style={{ display: "flex", alignItems: "center", gap: 5 }}
              >
                <IconPhone size={16} />
                {menuData.contactNo}
              </Anchor>
            )}
          </Flex>
        </Stack>

        <Divider mb="xl" />

        {/* Search Bar */}
        <Box mb="xl">
          <Paper p="md" radius="md" withBorder>
            <Stack gap="md">
              <TextInput
                placeholder="Search menu items by name, description, or category..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="lg"
                radius="md"
                leftSection={<IconSearch size={18} />}
                rightSection={
                  searchQuery && (
                    <ActionIcon
                      onClick={() => setSearchQuery("")}
                      variant="subtle"
                    >
                      <IconX size={16} />
                    </ActionIcon>
                  )
                }
              />

              <Group justify="space-between" align="flex-start" wrap="wrap">
                <Group gap="sm">
                  <Text size="sm" fw={500}>
                    Dietary:
                  </Text>
                  <Chip.Group
                    value={selectedDietary}
                    onChange={setSelectedDietary}
                    multiple
                  >
                    <Group gap="xs">
                      <Chip value="veg" color="green" variant="light">
                        🌱 Vegetarian
                      </Chip>
                      <Chip value="nonVeg" color="red" variant="light">
                        🍖 Non-Vegetarian
                      </Chip>
                    </Group>
                  </Chip.Group>
                </Group>

                {hasActiveFilters && (
                  <Button
                    variant="subtle"
                    size="xs"
                    onClick={clearFilters}
                    leftSection={<IconX size={14} />}
                  >
                    Clear Filters
                  </Button>
                )}
              </Group>

              {hasActiveFilters && (
                <Text size="sm" c="dimmed">
                  Found {filteredItems.length} item(s)
                </Text>
              )}
            </Stack>
          </Paper>
        </Box>

        {/* Categories Tabs with Horizontal Scroll */}
        <Box mb="xl" pos="relative">
          <Group justify="space-between" mb="sm">
            <Text fw={600} size="sm">
              Categories
            </Text>
            <Group gap="xs">
              <ActionIcon
                onClick={scrollLeft}
                variant="light"
                size="sm"
                radius="xl"
              >
                <IconArrowLeft size={14} />
              </ActionIcon>
              <ActionIcon
                onClick={scrollRight}
                variant="light"
                size="sm"
                radius="xl"
              >
                <IconArrowRight size={14} />
              </ActionIcon>
            </Group>
          </Group>

          <Box pos="relative">
            <ScrollArea
              viewportRef={scrollRef}
              type="hover"
              offsetScrollbars
              style={{ whiteSpace: "nowrap" }}
            >
              <Group
                gap="sm"
                wrap="nowrap"
                style={{
                  display: "inline-flex",
                  minWidth: "100%",
                  paddingBottom: 8,
                }}
              >
                {allCategories.map((category) => (
                  <Paper
                    key={category.name}
                    onClick={() => scrollToCategory(category.name)}
                    style={{
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      minWidth: "auto",
                      flexShrink: 0,
                    }}
                  >
                    <Stack
                      align="center"
                      gap={8}
                      p="sm"
                      style={{
                        backgroundColor:
                          activeCategory === category.name
                            ? colorScheme === "dark"
                              ? "#2c2c2c"
                              : "#fff"
                            : "transparent",
                        borderRadius: 12,
                        border:
                          activeCategory === category.name
                            ? `1px solid ${colorScheme === "dark" ? "#ff9e66" : "#ff8c4d"}`
                            : `1px solid ${colorScheme === "dark" ? "#333" : "#e0e0e0"}`,
                        transition: "all 0.2s ease",
                      }}
                    >
                      <Box
                        style={{
                          color:
                            activeCategory === category.name
                              ? "#ff8c4d"
                              : undefined,
                        }}
                      >
                        {category.icon}
                      </Box>
                      <Text
                        fw={activeCategory === category.name ? 700 : 500}
                        size="sm"
                        ta="center"
                        style={{
                          color:
                            activeCategory === category.name
                              ? "#ff8c4d"
                              : colorScheme === "dark"
                                ? "#ddd"
                                : "#333",
                        }}
                      >
                        {category.displayName}
                      </Text>
                      <Badge
                        size="sm"
                        variant={
                          activeCategory === category.name ? "filled" : "light"
                        }
                        color="orange"
                      >
                        {category.items.length}
                      </Badge>
                    </Stack>
                  </Paper>
                ))}
              </Group>
            </ScrollArea>
          </Box>
        </Box>

        {/* View Toggle and Results Count */}
        <Group justify="space-between" align="center" mb="md">
          <Text size="sm" c="dimmed">
            Showing {paginatedItems.length} of {filteredItems.length} items
          </Text>
          <SegmentedControl
            value={viewMode}
            onChange={(value) => setViewMode(value as "grid" | "list")}
            fullWidth
            styles={{
              label: {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                whiteSpace: "nowrap",
              },
            }}
            data={[
              {
                value: "grid",
                label: (
                  <Group gap={4} wrap="nowrap">
                    <IconLayoutGrid size={16} />
                    {!isMobile && <span>Grid</span>}
                  </Group>
                ),
              },
              {
                value: "list",
                label: (
                  <Group gap={4} wrap="nowrap">
                    <IconList size={16} />
                    {!isMobile && <span>List</span>}
                  </Group>
                ),
              },
            ]}
          />
        </Group>

        {/* Menu Items Display */}
        <Box>
          <Stack gap="xs" mb="lg" ta="center">
            <Title order={2} size="h2">
              {currentCategoryInfo?.displayName}
            </Title>
            {currentCategoryInfo?.description && !hasActiveFilters && (
              <Text c="dimmed" size="sm" maw={600} mx="auto">
                {currentCategoryInfo.description}
              </Text>
            )}
            {hasActiveFilters && (
              <Text c="dimmed" size="sm">
                Filtered results
              </Text>
            )}
            <Divider
              w={60}
              mx="auto"
              style={{ borderColor: "#ff8c4d", borderWidth: 2 }}
            />
          </Stack>

          {paginatedItems.length > 0 ? (
            <>
              {viewMode === "grid" ? (
                <SimpleGrid
                  cols={{ base: 1, sm: 2, md: 3, lg: 3 }}
                  spacing="lg"
                  verticalSpacing="lg"
                >
                  {paginatedItems.map((item) => (
                    <MenuItemCard key={item.id} item={item} />
                  ))}
                </SimpleGrid>
              ) : (
                /* Table View - Using MenuItemListItem component */
                <Paper radius="md" withBorder style={{ overflowX: "auto" }}>
                  <Table striped highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th style={{ width: 50 }}></Table.Th>
                        <Table.Th>Item Name</Table.Th>
                        <Table.Th style={{ width: 90 }}>Type</Table.Th>
                        <Table.Th style={{ width: 100 }}>Price</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {paginatedItems.map((item, idx) => (
                        <MenuItemListItem
                          key={item.id}
                          item={item}
                          index={idx}
                        />
                      ))}
                    </Table.Tbody>
                  </Table>
                </Paper>
              )}

              {/* Load more trigger */}
              {hasMore && (
                <Box ref={loadMoreRef} py="xl">
                  <Center>
                    {isLoading ? (
                      <Loader color="orange" />
                    ) : (
                      <Button
                        variant="light"
                        color="orange"
                        onClick={loadMore}
                        loading={isLoading}
                      >
                        Load More ({filteredItems.length - visibleItems}{" "}
                        remaining)
                      </Button>
                    )}
                  </Center>
                </Box>
              )}

              {!hasMore && filteredItems.length > ITEMS_PER_PAGE && (
                <Center py="xl">
                  <Text size="sm" c="dimmed">
                    All {filteredItems.length} items loaded
                  </Text>
                </Center>
              )}
            </>
          ) : (
            <Empty
              height={400}
              text={`No items found${searchQuery ? ` matching "${searchQuery}"` : ""}`}
            />
          )}
        </Box>
      </Container>

      <ChatAssistant
        restaurantId={
          menuData.id || (import.meta as any).env.VITE_RESTAURANT_ID
        }
      />
    </Box>
  );
};
